import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft, Plus, Trash2, Save, X, BookOpen,
  ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminStories = () => {
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedStory, setExpandedStory] = useState(null);
  const [filter, setFilter] = useState({ age_group: '', subject: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newStory, setNewStory] = useState({
    age_group: 'preschool',
    subject: '',
    title: '',
    scenes: [{ text: '', question: '', choices: ['', '', '', ''], correctChoice: 0, explanation: '' }]
  });

  useEffect(() => {
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin');
      return;
    }
    fetchStories();
  }, [navigate]);

  const fetchStories = async () => {
    try {
      const response = await axios.get(`${API}/admin/story`);
      setStories(response.data.stories);
    } catch (err) {
      console.error('Error fetching stories:', err);
    }
    setLoading(false);
  };

  const handleDelete = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    try {
      await axios.delete(`${API}/admin/story/${storyId}`);
      fetchStories();
    } catch (err) {
      alert('Error deleting story');
    }
  };

  const handleAddStory = async () => {
    try {
      await axios.post(`${API}/admin/story`, newStory);
      setShowAddForm(false);
      setNewStory({
        age_group: 'preschool',
        subject: '',
        title: '',
        scenes: [{ text: '', question: '', choices: ['', '', '', ''], correctChoice: 0, explanation: '' }]
      });
      fetchStories();
    } catch (err) {
      alert('Error creating story');
    }
  };

  const addScene = () => {
    setNewStory({
      ...newStory,
      scenes: [...newStory.scenes, { text: '', question: '', choices: ['', '', '', ''], correctChoice: 0, explanation: '' }]
    });
  };

  const updateScene = (index, field, value) => {
    const updated = [...newStory.scenes];
    if (field === 'choice') {
      updated[index].choices[value.index] = value.text;
    } else {
      updated[index][field] = value;
    }
    setNewStory({ ...newStory, scenes: updated });
  };

  const filteredStories = stories.filter(s => {
    if (filter.age_group && s.age_group !== filter.age_group) return false;
    if (filter.subject && !s.subject.includes(filter.subject)) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-white/20">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <BookOpen className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Story Content</h1>
                <p className="text-green-100">{stories.length} stories</p>
              </div>
            </div>
            <Button onClick={() => setShowAddForm(true)} className="bg-white text-green-600 hover:bg-green-50">
              <Plus className="w-4 h-4 mr-2" /> Add Story
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <select
            value={filter.age_group}
            onChange={(e) => setFilter({ ...filter, age_group: e.target.value })}
            className="px-4 py-2 rounded-lg border border-gray-300"
          >
            <option value="">All Age Groups</option>
            <option value="preschool">Preschool</option>
            <option value="elementary">Elementary</option>
            <option value="advanced">Advanced</option>
          </select>
          <Input
            placeholder="Search subject..."
            value={filter.subject}
            onChange={(e) => setFilter({ ...filter, subject: e.target.value })}
            className="max-w-xs"
          />
        </div>

        {/* Story List */}
        <div className="space-y-4">
          {filteredStories.map((story) => (
            <Card key={story.story_id} className="p-4 bg-white shadow" style={{ borderRadius: '12px' }}>
              <div 
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setExpandedStory(expandedStory === story.story_id ? null : story.story_id)}
              >
                <div>
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-700 mr-2">
                    {story.age_group}
                  </span>
                  <span className="px-2 py-1 rounded text-xs font-semibold bg-gray-100 text-gray-700 mr-2">
                    {story.subject}
                  </span>
                  <span className="font-bold text-gray-800">{story.title}</span>
                  <span className="text-gray-500 ml-2">({story.scenes?.length || 0} scenes)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleDelete(story.story_id); }}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                  {expandedStory === story.story_id ? <ChevronUp /> : <ChevronDown />}
                </div>
              </div>
              
              {expandedStory === story.story_id && (
                <div className="mt-4 space-y-3">
                  {story.scenes?.map((scene, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg">
                      <p className="font-semibold text-sm text-gray-700">Scene {index + 1}</p>
                      <p className="text-gray-800 mt-1">{scene.text}</p>
                      <p className="text-purple-600 mt-2 font-medium">Q: {scene.question}</p>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {scene.choices?.map((choice, i) => (
                          <span key={i} className={`px-2 py-1 rounded text-sm ${i === scene.correctChoice ? 'bg-green-100 text-green-700' : 'bg-gray-100'}`}>
                            {choice}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Add Story Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 bg-white" style={{ borderRadius: '16px' }}>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Add New Story</h2>
                <Button variant="ghost" onClick={() => setShowAddForm(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <select
                    value={newStory.age_group}
                    onChange={(e) => setNewStory({ ...newStory, age_group: e.target.value })}
                    className="px-3 py-2 rounded-lg border"
                  >
                    <option value="preschool">Preschool</option>
                    <option value="elementary">Elementary</option>
                    <option value="advanced">Advanced</option>
                  </select>
                  <Input
                    placeholder="Subject (e.g., math)"
                    value={newStory.subject}
                    onChange={(e) => setNewStory({ ...newStory, subject: e.target.value })}
                  />
                  <Input
                    placeholder="Story Title"
                    value={newStory.title}
                    onChange={(e) => setNewStory({ ...newStory, title: e.target.value })}
                  />
                </div>

                <div className="space-y-4">
                  {newStory.scenes.map((scene, sIndex) => (
                    <div key={sIndex} className="p-4 bg-gray-50 rounded-lg">
                      <p className="font-semibold mb-3">Scene {sIndex + 1}</p>
                      <textarea
                        placeholder="Story text..."
                        value={scene.text}
                        onChange={(e) => updateScene(sIndex, 'text', e.target.value)}
                        className="w-full p-2 border rounded-lg mb-2"
                        rows={3}
                      />
                      <Input
                        placeholder="Question"
                        value={scene.question}
                        onChange={(e) => updateScene(sIndex, 'question', e.target.value)}
                        className="mb-2"
                      />
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        {scene.choices.map((choice, cIndex) => (
                          <div key={cIndex} className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={`correct-${sIndex}`}
                              checked={scene.correctChoice === cIndex}
                              onChange={() => updateScene(sIndex, 'correctChoice', cIndex)}
                            />
                            <Input
                              placeholder={`Choice ${cIndex + 1}`}
                              value={choice}
                              onChange={(e) => updateScene(sIndex, 'choice', { index: cIndex, text: e.target.value })}
                              className="flex-1"
                            />
                          </div>
                        ))}
                      </div>
                      <Input
                        placeholder="Explanation for correct answer"
                        value={scene.explanation}
                        onChange={(e) => updateScene(sIndex, 'explanation', e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                <Button onClick={addScene} variant="outline" className="w-full">
                  <Plus className="w-4 h-4 mr-2" /> Add Scene
                </Button>

                <Button onClick={handleAddStory} className="w-full bg-green-600 hover:bg-green-700 text-white">
                  <Save className="w-4 h-4 mr-2" /> Save Story
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminStories;
